<template>
    <div class="grid-item grid-item-full">
        <div class="tile tile-secondary position-relative">
            <div class="tile-title">
                <h2>
                    <template v-if="oneTime">Past One-Time Orders</template>
                    <template v-else>
                        {{active? 'Active': ''}} {{pending? 'Pending': ''}} {{past? 'Past': ''}}
                        Subscriptions
                    </template>
                    <r-badge class="margin-left-s">{{subscriptions.length}}</r-badge>
                </h2>
            </div>
            <div class="tile-content no-padding" v-if="hasSubscriptions">
                <div class="tile-content" v-for="subscription in subscriptions" :key="subscription.id">
                    <div class="grid grid-unstackable">
                        <div class="grid-item">
                            <table class="table table-compact table-definition">
                                <colgroup>
                                    <col class="width-xxl">
                                </colgroup>
                                <tr>
                                    <td>Created</td>
                                    <td v-fs-block>{{subscription.createdTime | dateTime}}</td>
                                </tr>
                                <tr v-if="subscription.isPending">
                                    <td>Starts</td>
                                    <td v-fs-block>{{subscription.startTime | dateTime}}</td>
                                </tr>
                                <tr v-if="active || pending">
                                    <td>Renewal</td>
                                    <td v-fs-block>{{subscription.renewalTime | dateTime}}</td>
                                </tr>
                                <tr v-if="past">
                                    <td>Canceled</td>
                                    <td v-fs-block>{{subscription.canceledTime | dateTime}}</td>
                                </tr>
                                <tr>
                                    <td>Products</td>
                                    <td v-fs-block>{{nameExpanded(subscription.id, 'products') | join | coverEmpty}}</td>
                                </tr>
                                <tr>
                                    <td>Plans</td>
                                    <td v-fs-block>{{nameExpanded(subscription.id, 'plans') | join | coverEmpty}}</td>
                                </tr>
                                <tr v-if="subscription.isPast">
                                    <td>Reason</td>
                                    <td v-fs-block>{{subscription.cancelDescription | coverEmpty | unKebabCase}}</td>
                                </tr>
                            </table>
                        </div>
                        <div class="grid-item grid-item-quarter no-flex">
                            <v-popper2 class="no-flex" position="bottomEnd" :offset="8">
                                <template #trigger="{popper}">
                                    <v-icon-button class="no-padding" size="small" type="basic" @click="popper.toggle">
                                        <v-icon icon="actions"/>
                                    </v-icon-button>
                                </template>
                                <template #content>
                                    <div class="popover">
                                        <div class="popover-control">
                                            <div class="popover-content popover-menu">
                                                <template v-if="!subscription.isPast">
                                                    <template v-if="subscription.isActive && !oneTime">
                                                        <router-link
                                                            v-if="$auth.invoices.read.isAllowed"
                                                            class="popover-menu-item"
                                                            :to="{
                                                                name: 'order-upcoming-invoice',
                                                                params: {
                                                                    id: subscription.id,
                                                                }
                                                            }">View</router-link>
                                                    </template>
                                                    <template v-else>
                                                        <router-link
                                                            v-if="$auth.invoices.read.isAllowed"
                                                            class="popover-menu-item"
                                                            :to="{
                                                                name: 'invoice-details',
                                                                params: {
                                                                    id: subscription.$links.initialInvoice.entityId,
                                                                }
                                                            }"
                                                        >View</router-link>
                                                    </template>
                                                    <a v-if="$auth.subscriptions.edit.isAllowed && subscription.isActive && !oneTime"
                                                       class="popover-menu-item popover-menu-item-negative"
                                                       @click="startCancelOrder(subscription)"
                                                    >Cancel Subscription</a>
                                                </template>
                                                <template v-else>
                                                    <a v-if="$auth.subscriptions.edit.isAllowed"
                                                       class="popover-menu-item"
                                                       @click="startReactivateOrder(subscription)"
                                                    >Reactivate Subscription</a>
                                                </template>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                            </v-popper2>
                        </div>
                    </div>
                    <cancel-order-modal :order="order" v-if="showCancelModal" @close="stopCancelOrder"/>
                    <reactivate-order-modal :order="order" v-if="showReactiveModel" @close="stopReactivateOrder"/>
                </div>
            </div>
            <div class="tile-content" v-else>
                <p class="text-muted" v-if="active">This customer does not have any active subscriptions.</p>
                <p class="text-muted" v-else-if="pending">This customer does not have any pending subscriptions.</p>
                <p class="text-muted" v-else-if="oneTime">This customer does not have any one-time orders.</p>
                <p class="text-muted" v-else>This customer does not have any past subscriptions.</p>
            </div>
        </div>
    </div>
</template>

<script>
    import {mapState, mapGetters} from 'vuex';
    import vPopper2 from '@/components/popper/v-popper2';
    import {vIconButton, vIcon} from '@/components/form';
    import orderSummaryActions from '@/modules/mixins/order-summary/order-summary-actions';
    import ProductModel from '@/common/models/product-model';
    import PlanModel from '@/common/models/plan-model';

    export default {
        components: {
            vPopper2,
            vIconButton,
            vIcon,
            cancelOrderModal: () => import('@/modules/orders/components/order-summary/cancel-modal'),
            reactivateOrderModal: () => import('@/modules/orders/components/order-summary/reactivate-modal'),
        },
        mixins: [orderSummaryActions],
        props: {
            active: Boolean,
            pending: Boolean,
            past: Boolean,
            oneTime: Boolean,
        },
        computed: {
            ...mapState('customers', ['customer']),
            ...mapGetters({
                activeSubscriptionOrders: 'customersOrders/activeSubscriptionOrders',
                pendingOrders: 'customersOrders/pendingOrders',
                pastOrders: 'customersOrders/pastOrders',
                pastOneTimeOrders: 'customersOrders/pastOneTimeOrders',
            }),
            subscriptions() {
                if (this.active) {
                    return this.activeSubscriptionOrders;
                }
                if (this.oneTime) {
                    return this.pastOneTimeOrders;
                }
                if (this.pending) {
                    return this.pendingOrders;
                }
                return this.pastOrders;
            },
            hasSubscriptions() {
                return this.$filters.isNotEmpty(this.subscriptions);
            },
        },
        watch: {
            subscriptions: {
                immediate: true,
                handler(newValue) {
                    if (this.$filters.isNotEmpty(newValue)) {
                        this.fetchProductsAndPlans();
                    }
                },
            },
        },
        data() {
            return {
                isLoading: false,
                products: [],
                plans: [],
                order: null,
                showCancelModal: false,
                showReactiveModel: false,
                subscriptionsProductsPlansMap: {},
            };
        },
        methods: {
            startReactivateOrder(subscription) {
                this.order = subscription;
                this.showReactiveModel = true;
            },
            startCancelOrder(subscription) {
                this.order = subscription;
                if (this.order) {
                    this.showCancelModal = true;
                }
            },
            async fetchProductsAndPlans() {
                this.$startLoading();
                this.isLoading = true;
                const subscriptionsPlanIdsMap = {};
                this.subscriptions.forEach((subscription) => {
                    subscriptionsPlanIdsMap[subscription.id] = subscription.items.map(item => item.planId);
                });
                const flattenPlanIds = [].concat(...Object.values(subscriptionsPlanIdsMap));
                const planParams = {
                    filter: `id:${[...new Set(flattenPlanIds)].join()}`,
                };
                const {items: planItems} = await this.$api(({plans}) => plans.getAll(planParams));
                this.plans = planItems.map(({fields}) => new PlanModel(fields));
                const planProductMap = {};
                this.plans.forEach((plan) => {
                    planProductMap[plan.id] = plan.productId;
                });
                const productParams = {
                    filter: `id:${[...new Set(Object.values(planProductMap))].join()}`,
                };
                const {items: productItems} = await this.$api(({products}) => products.getAll(productParams));
                this.products = productItems.map(({fields}) => new ProductModel(fields));
                this.subscriptions.forEach((subscription) => {
                    const plans = subscriptionsPlanIdsMap[subscription.id].map(planId => this.plans.find(plan => plan.id === planId));
                    const products = plans.map(plan => this.products.find(product => product.id === plan.productId));
                    this.subscriptionsProductsPlansMap[subscription.id] = {
                        plans,
                        products,
                    };
                });
                this.isLoading = false;
                this.$stopLoading();
            },
            stopCancelOrder() {
                this.order = null;
                this.showCancelModal = false;
            },
            stopReactivateOrder() {
                this.order = null;
                this.showReactiveModel = false;
            },
            orderSummaryRefreshOrder() {
                this.$emit('refresh-subscriptions');
            },
            nameExpanded(subscriptionId, property) {
                if (subscriptionId && !this.isLoading) {
                    return this.subscriptionsProductsPlansMap[subscriptionId][property].map(item => `${item.name} (${item.id})`);
                }
                return [];
            },
        },
    };
</script>
